import React from "react";
import { createRoot } from "react-dom/client";
import { MemoryRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import chromeTheme from "../constants/chromeTheme";
import SchoolNavbar from "../components/School/Navbar";
import SchoolNavContents from "../components/School/SchoolNavContents";
import SchoolYearSelect from "../components/School/SchoolYearSelect";
import Footer from "../components/Footer";
import { SnackAlertProvider, SnackAlert } from "../contexts/SnackAlertContext";
import { FixedSidebarProvider } from "../contexts/FixedSidebarContext";
import { BookmarksProvider } from "../contexts/BookmarksContext";
import { NightModeProvider, useNightMode } from "../contexts/NightModeContext";
// Registers window.AeChrome.mountPageHeader so legacy v1 pages can mount a v2
// page header into their own content. Side-effect import: runs as the chrome
// bundle evaluates, before DOMContentLoaded and before the v1 SPA mounts.
import "../chrome/mountPageHeader";

const LEGACY_SCHOOL_YEAR_STORAGE_KEY = "current_school_year";

function LegacyNavbar({ appProps, schoolSlug, currentYearId, showNavDrawer }) {
  const { night } = useNightMode();

  const handleSchoolYearChange = (e) => {
    const newId = e.target.value;
    if (!newId || !schoolSlug) return;
    localStorage.setItem(LEGACY_SCHOOL_YEAR_STORAGE_KEY, newId);
    window.location.replace(`${window.location.pathname}?school_year=${newId}`);
  };

  return (
    <SchoolNavbar
      appProps={appProps}
      schoolSlug={schoolSlug}
      navigationMode="browser"
      linkComponent="a"
      linkProps={{ href: schoolSlug ? `/v2/school/${schoolSlug}` : "/v2" }}
      showNavDrawer={showNavDrawer}
      leftExtras={
        <SchoolYearSelect
          years={appProps?.school_years}
          value={currentYearId}
          onChange={handleSchoolYearChange}
          // Match the SPA: white year select on the dark night navbar.
          sx={
            night
              ? { color: "#FFFFFF", "& .MuiSelect-icon": { color: "#FFFFFF" } }
              : undefined
          }
        />
      }
    />
  );
}

document.addEventListener("DOMContentLoaded", () => {
  const propsScript = document.getElementById("app-props");
  const appProps = propsScript
    ? JSON.parse(propsScript.textContent || "null")
    : null;
  if (!appProps) return;

  // Only render on School/MainLayout pages (not Guardian/Student portals)
  if (!window.location.pathname.startsWith("/school/")) return;

  const navbarNode = document.getElementById("v2-navbar");
  const sidebarNode = document.getElementById("v2-sidebar");
  const footerNode = document.getElementById("v2-footer");

  const schoolSlug = appProps?.school?.slug ?? null;
  const fallbackYearId = appProps?.school_year?.id
    ? String(appProps.school_year.id)
    : "";
  const storedYearId = localStorage.getItem(LEGACY_SCHOOL_YEAR_STORAGE_KEY);
  const currentYearId = storedYearId || fallbackYearId;

  // The pre-paint inline script in the layout made a provisional sidebar vs
  // drawer call from the localStorage cache (to reserve the body offset before
  // first paint). The backend value in appProps is the real source of truth,
  // and the cache can be stale (toggled on another device, another staff user
  // on the same browser, a lost persist). Reconcile before mounting: correct
  // the class so the chrome never renders a sidebar the preference says is off.
  // Keep the breakpoint in sync with the inline script and FixedSidebarContext.
  const sidebarMode =
    appProps?.use_fixed_sidebar === true &&
    window.matchMedia("(min-width: 900px)").matches;
  document.documentElement.classList.toggle("v2-fixed-sidebar", sidebarMode);

  // Each chrome piece is its own React root over a plain legacy document, so the
  // switch cannot flip the layout via shared state. Persist + reload instead:
  // the reload re-runs the pre-paint script and this entry with the new choice.
  // Night mode joins this pattern: each root reads it from localStorage at mount,
  // and the footer switch reloads so the separate navbar root picks up the flip.
  const wrap = (children) => (
    <ThemeProvider theme={chromeTheme}>
      <FixedSidebarProvider
        initialEnabled={appProps?.use_fixed_sidebar}
        onChange={() => window.location.reload()}
      >
        <NightModeProvider onChange={() => window.location.reload()}>
          <BookmarksProvider initial={appProps?.bookmarks ?? []}>
            <MemoryRouter>
              <SnackAlertProvider>
                {children}
                <SnackAlert />
              </SnackAlertProvider>
            </MemoryRouter>
          </BookmarksProvider>
        </NightModeProvider>
      </FixedSidebarProvider>
    </ThemeProvider>
  );

  if (navbarNode) {
    createRoot(navbarNode).render(
      wrap(
        <LegacyNavbar
          appProps={appProps}
          schoolSlug={schoolSlug}
          currentYearId={currentYearId}
          showNavDrawer={!sidebarMode}
        />
      )
    );
  }

  if (sidebarMode && sidebarNode) {
    createRoot(sidebarNode).render(
      wrap(
        <SchoolNavContents
          appProps={appProps}
          schoolSlug={schoolSlug}
          schoolYearId={currentYearId}
          navigationMode="browser"
          isMobile={false}
        />
      )
    );
  }

  if (footerNode) {
    createRoot(footerNode).render(
      <ThemeProvider theme={chromeTheme}>
        <NightModeProvider onChange={() => window.location.reload()}>
          <Footer />
        </NightModeProvider>
      </ThemeProvider>
    );
  }
});
